import type { ReactNode } from "react";
import { Icon, Tooltip } from "@bloo-team/ui";
import { Rows2, Rows4, ZoomIn } from "@bloo-team/ui/icons";
import { useDensity } from "../density";
import { UI_SCALE_PREFS, useUiScale, type UiScalePref } from "../ui-scale";

const SCALE_LABEL: Record<UiScalePref, string> = {
  auto: "auto",
  "1": "100%",
  "1.25": "125%",
  "1.5": "150%",
};

export interface StripStat {
  label: string;
  value: string;
  dir?: "up" | "down" | "flat";
  good?: boolean;
  severity?: "critical" | "high" | "medium" | "low";
}

export interface PageStripProps {
  title: string;
  stats?: StripStat[];
  /** Right-aligned context, e.g. "gotham · last 24h". */
  meta?: string;
  /** Right-edge extras (time-range control lands here). */
  end?: ReactNode;
}

/**
 * The compact status strip that replaced the h1 page header (density
 * redesign): ~40px carrying the page label plus live KPIs, instead of ~20% of
 * the viewport stating a location the rail already shows. Every screen
 * inherits it; the density toggle lives here because this is where the
 * decision shows.
 */
export function PageStrip({ title, stats = [], meta, end }: PageStripProps) {
  const [density, setDensity] = useDensity();
  const [scale, setScale, factor] = useUiScale();
  const nextScale =
    UI_SCALE_PREFS[
      (UI_SCALE_PREFS.indexOf(scale) + 1) % UI_SCALE_PREFS.length
    ]!;
  const scaleNow =
    scale === "auto"
      ? `auto (${Math.round(factor * 100)}%)`
      : SCALE_LABEL[scale];
  return (
    <header className="strip">
      <h1 className="strip-title">{title}</h1>
      {stats.length ? <span className="strip-sep" aria-hidden="true" /> : null}
      <div className="strip-stats">
        {stats.map((s) => (
          <span
            key={s.label}
            className="strip-stat"
            data-severity-inline={s.severity}
          >
            <span className="strip-stat-label">{s.label}</span>
            <span
              className="strip-stat-value hud-delta"
              data-dir={s.dir ?? "flat"}
              data-good={String(s.good ?? true)}
            >
              {s.value}
            </span>
          </span>
        ))}
      </div>
      <span className="strip-spacer" />
      <span className="strip-end">
        {meta ? <span className="strip-meta">{meta}</span> : null}
        {end}
        <span className="strip-btns">
          <Tooltip
            title={`UI scale · ${scaleNow} — next: ${SCALE_LABEL[nextScale]}`}
            placement="bottom"
          >
            <button
              type="button"
              className="strip-btn"
              aria-label={`UI scale ${scaleNow}, switch to ${SCALE_LABEL[nextScale]}`}
              data-scale={scale}
              onClick={() => setScale(nextScale)}
            >
              <Icon icon={ZoomIn} size={14} />
            </button>
          </Tooltip>
          <Tooltip
            title={
              density === "compact" ? "Comfortable density" : "Compact density"
            }
            placement="bottom"
          >
            <button
              type="button"
              className="strip-btn"
              aria-label={`Switch to ${density === "compact" ? "comfortable" : "compact"} density`}
              onClick={() =>
                setDensity(density === "compact" ? "comfortable" : "compact")
              }
            >
              <Icon icon={density === "compact" ? Rows2 : Rows4} size={14} />
            </button>
          </Tooltip>
        </span>
      </span>
    </header>
  );
}
